// PointsDisplay.js
// Display user's points, level, streak, and achievements

import React, { useState, useEffect } from 'react';
import { Trophy, Star, Flame, Award, TrendingUp, Target, Zap } from 'lucide-react';
import { getUserPoints, getUserAchievements, pointsForNextLevel } from './pointsSystem';

const PointsDisplay = ({ userId, darkMode, compact = false }) => {
  const [userPoints, setUserPoints] = useState({ total_points: 0, level: 1, streak_days: 0 });
  const [achievements, setAchievements] = useState([]);
  const [showAchievements, setShowAchievements] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadPointsData();
    }
  }, [userId]);

  const loadPointsData = async () => {
    setLoading(true);
    const points = await getUserPoints(userId);
    const userAchievements = await getUserAchievements(userId);

    if (points) {
      setUserPoints(points);
    }

    setAchievements(userAchievements);
    setLoading(false);
  };

  const getProgressToNextLevel = () => {
    const currentLevel = userPoints.level;
    const currentPoints = userPoints.total_points;
    const pointsForCurrent = Math.pow(currentLevel - 1, 2) * 100;
    const pointsForNext = pointsForNextLevel(currentLevel);
    const progress = ((currentPoints - pointsForCurrent) / (pointsForNext - pointsForCurrent)) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  const getStreakColor = (days) => {
    if (days >= 30) return 'text-purple-500';
    if (days >= 7) return 'text-orange-500';
    if (days >= 3) return 'text-yellow-500';
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${compact ? 'p-2' : 'p-4'} rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        {/* Points Badge */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
          <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
          <span className={`font-bold text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            {userPoints.total_points}
          </span>
        </div>

        {/* Level Badge */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
          <Trophy className="w-4 h-4 text-blue-500" />
          <span className={`font-bold text-sm ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
            Lv {userPoints.level}
          </span>
        </div>

        {/* Streak Badge */}
        {userPoints.streak_days > 0 && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${darkMode ? 'bg-orange-900/30 border border-orange-700' : 'bg-orange-100 border border-orange-300'}`}>
            <Flame className={`w-4 h-4 ${getStreakColor(userPoints.streak_days)}`} fill="currentColor" />
            <span className={`font-bold text-sm ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>
              {userPoints.streak_days}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-lg`}>
      <div className="flex justify-between items-start mb-6">
        <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Your Progress
        </h3>
        <button
          onClick={() => setShowAchievements(!showAchievements)}
          className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-all`}
        >
          <Award className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Total Points */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/20 border border-yellow-700/50' : 'bg-yellow-50 border border-yellow-200'}`}>
          <Star className="w-8 h-8 text-yellow-500 mb-2" fill="currentColor" />
          <div className={`text-3xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            {userPoints.total_points}
          </div>
          <div className={`text-sm ${darkMode ? 'text-yellow-300/70' : 'text-yellow-600'}`}>Points</div>
        </div>

        {/* Level */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/20 border border-blue-700/50' : 'bg-blue-50 border border-blue-200'}`}>
          <Trophy className="w-8 h-8 text-blue-500 mb-2" />
          <div className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
            {userPoints.level}
          </div>
          <div className={`text-sm ${darkMode ? 'text-blue-300/70' : 'text-blue-600'}`}>Level</div>
        </div>

        {/* Streak */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-orange-900/20 border border-orange-700/50' : 'bg-orange-50 border border-orange-200'}`}>
          <Flame className={`w-8 h-8 mb-2 ${getStreakColor(userPoints.streak_days)}`} fill="currentColor" />
          <div className={`text-3xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>
            {userPoints.streak_days}
          </div>
          <div className={`text-sm ${darkMode ? 'text-orange-300/70' : 'text-orange-600'}`}>Day Streak</div>
        </div>
      </div>

      {/* Level Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Progress to Level {userPoints.level + 1}
          </span>
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {Math.round(getProgressToNextLevel())}%
          </span>
        </div>
        <div className={`w-full h-3 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 rounded-full"
            style={{ width: `${getProgressToNextLevel()}%` }}
          />
        </div>
        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {pointsForNextLevel(userPoints.level) - userPoints.total_points} points to next level
        </div>
      </div>

      {/* Achievements */}
      {showAchievements && (
        <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-4`}>
          <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Achievements ({achievements.length})
          </h4>
          {achievements.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}
                >
                  <div className="text-2xl mb-1">{achievement.achievements.icon}</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {achievement.achievements.name}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    +{achievement.achievements.points_reward} pts
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No achievements yet. Keep learning!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PointsDisplay;
